import type { Overview } from "../lib/types";
import { formatCount } from "../lib/money";
import { useTranslation } from "react-i18next";

/**
 * Clicks per day, as bars.
 *
 * Drawn as divs rather than pulled from a charting library, which would be
 * ~100KB to render one series of at most ninety values. The same reasoning the
 * icon set is inlined under.
 *
 * The scale is the window's own maximum rather than a fixed ceiling: an
 * affiliate with four clicks a day should see the shape of their week, not four
 * invisible bars under a scale set for somebody else's traffic.
 */
export function ClickChart({ series }: { series: Overview["clicksByDay"] }) {
  const { t } = useTranslation("charts");
  const peak = series.reduce((max, point) => Math.max(max, point.clicks), 0);

  if (peak === 0) {
    return (
      <p className="px-4 py-10 text-center text-[13px] text-fg-subtle">
        {t("noClicks")}
      </p>
    );
  }

  return (
    <div className="px-4 py-4">
      {/*
       * Every level of this needs a *definite* height, which is the one thing
       * about a percentage-height bar that is easy to get wrong and invisible in
       * review: `flex-1` sizes a flex item along the main axis, so a column
       * wrapper laid out in a row gets a width and a content-derived height. A
       * bar inside it asking for `height: 40%` then resolves against `auto`,
       * computes to zero, and the chart renders as an empty box with axis labels
       * under it — which looks like a data problem rather than a layout one.
       *
       * So the wrapper is `h-full` against the fixed `h-28` above it, and is
       * itself a column that pushes its bar to the bottom.
       */}
      {/* A hairline under the series, so the bars stand on something rather
          than floating above the axis labels. */}
      <div className="flex h-28 items-end gap-[3px] border-b border-line">
        {series.map((point) => (
          <div
            key={point.day}
            className="group flex h-full flex-1 flex-col justify-end"
            /* The native tooltip rather than a hover card: the value is one
               number and a day, and a positioned overlay for that is a
               component to maintain and a thing to get wrong on a phone, where
               there is no hover at all. */
            title={t("clicksOnDay", { day: point.day, count: formatCount(point.clicks) })}
          >
            {/*
             * A solid bar, not a tint.
             *
             * This was `bg-accent/40`, carried over from a dark theme where a
             * 40% accent sits *above* a near-black ground and stays saturated.
             * Over white the same value washes to a pale lilac and the series
             * reads as decoration rather than as data. 70% keeps it plainly the
             * accent, and dark enough that a one-click day is still visible at
             * eight pixels wide.
             */}
            <div
              className="w-full rounded-t-xs bg-accent/70 transition-colors duration-200 ease-smooth group-hover:bg-accent"
              /* A floor of 2px, so a day with one click is visibly a day with
                 one click rather than indistinguishable from a day with none.
                 The zero case still renders nothing. */
              style={{
                height: point.clicks === 0 ? 0 : `${Math.max(2, (point.clicks / peak) * 100)}%`,
              }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-fg-subtle">
        <span>{series[0]?.day ?? ""}</span>
        <span>{series.at(-1)?.day ?? ""}</span>
      </div>
    </div>
  );
}
